<template>
    <BaseSidebar
        :is-open="showPointsHistorySidebar"
        :on-close="closePointsHistorySidebar"
        :show-header="false"
    >
        <!-- Custom Header with Navigation -->
        <div
            class="flex justify-between items-center px-8 pt-6 pb-4 sticky top-0 bg-white dark:bg-slate-800 z-[25]"
        >
            <div class="flex items-center gap-2">
                <button
                    type="button"
                    class="flex p-2 items-start gap-2 rounded-md hover:bg-slate-100 dark:hover:bg-slate-700"
                    :class="{ 'opacity-50 cursor-not-allowed': isFirstProgram }"
                    @click="navigateToPreviousProgram"
                    :disabled="isFirstProgram"
                >
                    <Icon icon="chevron-up" :width="20" :height="20" />
                </button>
                <button
                    type="button"
                    class="flex p-2 items-start gap-2 rounded-md hover:bg-slate-100 dark:hover:bg-slate-700"
                    :class="{ 'opacity-50 cursor-not-allowed': isLastProgram }"
                    @click="navigateToNextProgram"
                    :disabled="isLastProgram"
                >
                    <Icon icon="chevron-down" :width="20" :height="20" />
                </button>
                <span class="text-slate-400 dark:text-slate-500 text-sm font-normal leading-5 pl-2">
                    {{ currentProgramIndex }} of {{ totalPrograms }} programs
                </span>
            </div>
            <button
                type="button"
                class="p-2 rounded-md text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700"
                @click="closePointsHistorySidebar"
            >
                <Icon icon="close" :width="24" :height="24" />
            </button>
        </div>

        <div v-if="selectedProgramForHistory" class="px-8 pb-8">
            <ProgramCard
                :program="selectedProgramForHistory"
                :formatted-cpp="formattedCpp"
                :cpp-tooltip-text="cppTooltipText"
                :program-type-name="programTypeName"
                :get-program-image-url="getProgramImageUrl"
            />

            <ProgramActions
                :program="selectedProgramForHistory"
                :is-manual-account="isManualAccount"
                :is-synced-account="isSyncedAccount"
                :can-view-about-program="canViewAboutProgram"
                :transfer-partners-page-url="transferPartnersPageUrl"
                @update-points="handleUpdatePoints"
                @settings="handleSettings"
                @delete-program="handleDeleteProgram"
            />

            <ProgramDetails
                :program="selectedProgramForHistory"
                :user="selectedUserForHistory"
                :update-type="updateType"
                :last-updated="lastUpdated"
                :added-date="addedDate"
            />

            <CardBreakdown
                :program="selectedProgramForHistory"
                :cards="cardBreakdownData"
                :total="cardBreakdownTotal"
                :should-show="shouldShowCardBreakdown"
                :get-program-image-url="getProgramImageUrl"
            />

            <TransferPartners
                :partners="bestTransferRates || []"
                :loading="loadingBestTransferRates"
                :has-partners="hasBestTransferRates"
                :transfer-partners-page-url="transferPartnersPageUrl"
                :enabled="transferPartnersGuideEnabled"
                :program-img-src="programImgSrc"
                :format-program-type="formatProgramType"
            />

            <PointsHistory
                :history-data="pointsHistoryData"
                :loading="loadingPointsHistory"
                :is-manual-account="isManualAccount"
                :selected-period="selectedPeriod"
                :periods="periods"
                :grouped-history="groupedHistory"
                :history-list="historyList"
                :has-history-data="hasHistoryData"
                :total-records="totalHistoryRecords"
                :has-more-records="hasMoreRecords"
                :remaining-records="remainingRecords"
                :display-period="displayPeriod"
                :sidebar-open="showPointsHistorySidebar"
                @select-period="selectPeriod"
                @navigate-prev="navigatePeriodPrev"
                @navigate-next="navigatePeriodNext"
                @show-more="showMore"
                @edit-entry="openEditHistoryModal"
                @delete-entry="openDeleteHistoryModal"
            />
        </div>
    </BaseSidebar>

    <EditHistoryModal
        :show="showEditHistoryModal"
        :program="selectedProgramForHistory"
        :edit-balance="editBalance"
        :edit-balance-date="editBalanceDate"
        :date-config="dateConfig"
        :get-program-image-url="getProgramImageUrl"
        @close="closeEditHistoryModal"
        @confirm="confirmEditHistoryEntry"
        @update:edit-balance="handleEditBalanceInput"
        @update:edit-balance-date="(val) => (editBalanceDate = val)"
    />

    <DeleteHistoryModal
        :show="showDeleteHistoryModal"
        :entry="deletingHistoryEntry"
        @cancel="closeDeleteHistoryModal"
        @confirm="confirmDeleteHistoryEntry"
    />
</template>

<script setup>
import BaseSidebar from '@/components/ui/base-sidebar.vue';
import Icon from '@/components/ui/icon/icon.vue';
import ProgramCard from '@/components/mypoints/points-sidebar/ProgramCard.vue';
import ProgramActions from '@/components/mypoints/points-sidebar/ProgramActions.vue';
import ProgramDetails from '@/components/mypoints/points-sidebar/ProgramDetails.vue';
import CardBreakdown from '@/components/mypoints/points-sidebar/CardBreakdown.vue';
import TransferPartners from '@/components/mypoints/points-sidebar/TransferPartners.vue';
import PointsHistory from '@/components/mypoints/points-sidebar/PointsHistory.vue';
import EditHistoryModal from '@/components/mypoints/points-sidebar/EditHistoryModal.vue';
import DeleteHistoryModal from '@/components/mypoints/points-sidebar/DeleteHistoryModal.vue';
import { usePointsSidebar } from '@/composables/usePointsSidebar';

const {
    // Store refs
    showPointsHistorySidebar,
    selectedProgramForHistory,
    selectedUserForHistory,
    pointsHistoryData,
    loadingPointsHistory,
    loadingBestTransferRates,
    bestTransferRates,

    // Local state
    selectedPeriod,
    periods,
    dateConfig,
    showEditHistoryModal,
    editBalance,
    editBalanceDate,
    showDeleteHistoryModal,
    deletingHistoryEntry,

    // Computed
    displayPeriod,
    hasHistoryData,
    formattedCpp,
    cppTooltipText,
    programTypeName,
    updateType,
    isManualAccount,
    isSyncedAccount,
    canViewAboutProgram,
    lastUpdated,
    addedDate,
    historyList,
    groupedHistory,
    currentProgramIndex,
    totalPrograms,
    isFirstProgram,
    isLastProgram,
    totalHistoryRecords,
    hasMoreRecords,
    remainingRecords,
    hasBestTransferRates,
    cardBreakdownData,
    shouldShowCardBreakdown,
    cardBreakdownTotal,
    transferPartnersGuideEnabled,
    transferPartnersPageUrl,

    // Methods
    closePointsHistorySidebar,
    getProgramImageUrl,
    programImgSrc,
    formatProgramType,
    handleDeleteProgram,
    handleSettings,
    selectPeriod,
    navigatePeriodPrev,
    navigatePeriodNext,
    showMore,
    navigateToPreviousProgram,
    navigateToNextProgram,
    handleUpdatePoints,
    openEditHistoryModal,
    closeEditHistoryModal,
    confirmEditHistoryEntry,
    handleEditBalanceInput,
    openDeleteHistoryModal,
    closeDeleteHistoryModal,
    confirmDeleteHistoryEntry,
} = usePointsSidebar();
</script>
